import React from "react";
import { RegisteredBodyProfile } from "../../../../types/cdd-records.types";
import { formatDateShort } from "../../../../utils/dateFormatters";
import { getLicenceStatusClass } from "./entityProfileConfig";

interface RegisteredBodyProfileComponentProps {
  profile: RegisteredBodyProfile;
  onEdit?: () => void;
}

export const RegisteredBodyProfileComponent: React.FC<
  RegisteredBodyProfileComponentProps
> = ({ profile, onEdit }) => {
  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Registered Body Profile
          </h3>
          {onEdit && (
            <button
              onClick={onEdit}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Edit
            </button>
          )}
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">
                Basic Information
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500">Name</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {profile.name}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Status</dt>
                  <dd>
                    <span
                      className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                        profile.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {profile.status}
                    </span>
                  </dd>
                </div>
                {profile.regulatoryBody && (
                  <div>
                    <dt className="text-xs text-gray-500">Regulatory Body</dt>
                    <dd className="text-sm text-gray-900">
                      {profile.regulatoryBody}
                    </dd>
                  </div>
                )}
                {profile.governingLegislation && (
                  <div>
                    <dt className="text-xs text-gray-500">
                      Governing Legislation
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {profile.governingLegislation}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Registration Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">
                Registration
              </h4>
              <dl className="space-y-3">
                {profile.registration.abn && (
                  <div>
                    <dt className="text-xs text-gray-500">ABN</dt>
                    <dd className="text-sm font-mono text-gray-900">
                      {profile.registration.abn}
                    </dd>
                  </div>
                )}
                {profile.registration.acn && (
                  <div>
                    <dt className="text-xs text-gray-500">ACN</dt>
                    <dd className="text-sm font-mono text-gray-900">
                      {profile.registration.acn}
                    </dd>
                  </div>
                )}
                {profile.registration.arbn && (
                  <div>
                    <dt className="text-xs text-gray-500">ARBN</dt>
                    <dd className="text-sm font-mono text-gray-900">
                      {profile.registration.arbn}
                    </dd>
                  </div>
                )}
                {profile.registration.registrationDate && (
                  <div>
                    <dt className="text-xs text-gray-500">Registration Date</dt>
                    <dd className="text-sm text-gray-900">
                      {formatDateShort(profile.registration.registrationDate)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Licences Overview */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase">
                Licences Overview
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500">Total Licences</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {profile.regulatoryLicences.length}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Active Licences</dt>
                  <dd className="text-sm text-gray-900">
                    {
                      profile.regulatoryLicences.filter(
                        (l) => l.status === "current",
                      ).length
                    }
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Expiring Soon</dt>
                  <dd className="text-sm text-gray-900">
                    {
                      profile.regulatoryLicences.filter((l) => {
                        if (!l.expiryDate) return false;
                        const expiry = new Date(l.expiryDate);
                        const thirtyDays = new Date();
                        thirtyDays.setDate(thirtyDays.getDate() + 30);
                        return expiry <= thirtyDays && expiry > new Date();
                      }).length
                    }
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Registered Address */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Registered Address
            </h4>
            <address className="text-sm text-gray-600 not-italic">
              {profile.registeredAddress.line1}
              <br />
              {profile.registeredAddress.line2 && (
                <>
                  {profile.registeredAddress.line2}
                  <br />
                </>
              )}
              {profile.registeredAddress.suburb},{" "}
              {profile.registeredAddress.state}{" "}
              {profile.registeredAddress.postcode}
              <br />
              {profile.registeredAddress.country}
            </address>
          </div>

          {/* Contact Details */}
          {profile.contactDetails && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Contact Details
              </h4>
              <div className="flex flex-wrap gap-6 text-sm">
                {profile.contactDetails.email && (
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <a
                      href={`mailto:${profile.contactDetails.email}`}
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      {profile.contactDetails.email}
                    </a>
                  </div>
                )}
                {profile.contactDetails.phone && (
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <a
                      href={`tel:${profile.contactDetails.phone}`}
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      {profile.contactDetails.phone}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Regulatory Licences */}
      {profile.regulatoryLicences.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Regulatory Licences
              </h3>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded">
                {profile.regulatoryLicences.length} licences
              </span>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {profile.regulatoryLicences.map((licence, index) => (
                <div
                  key={licence.licenceNumber || index}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900">
                          {licence.licenceType}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded border ${getLicenceStatusClass(licence.status)}`}
                        >
                          {licence.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-mono">
                          {licence.licenceNumber}
                        </span>
                        <span className="ml-2 text-gray-500">
                          • {licence.licenceHolderName}
                        </span>
                      </div>
                      <div className="flex gap-4 text-sm text-gray-500">
                        {licence.issueDate && (
                          <span>
                            Issued: {formatDateShort(licence.issueDate)}
                          </span>
                        )}
                        {licence.expiryDate && (
                          <span>
                            Expires: {formatDateShort(licence.expiryDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>

                  {/* Conditions */}
                  {licence.conditions && licence.conditions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 font-medium mb-1">
                        Conditions:
                      </p>
                      <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                        {licence.conditions.map((condition, idx) => (
                          <li key={idx}>{condition}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Authorisations */}
                  {licence.authorisations &&
                    licence.authorisations.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500 font-medium mb-1">
                          Authorisations:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {licence.authorisations.map((auth, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded"
                            >
                              {auth}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisteredBodyProfileComponent;
